import type {
  Deductible,
  Extras,
  HaftpflichtProduct,
  InsuranceCover,
  KaskoProduct,
  MotorContractCoverageChange
} from '@/typesTenant/MotorContractChange'
import { ComprehensiveCover } from '@/typesTenant/MotorContractChange'
import type { Fahrerkreis, MotorContract } from '@/types/motor/MotorContract'
import type {
  HaftpflichtExtras,
  KaskoExtras,
  Teilkasko,
  VersicherungsschutzHaftpflicht,
  VersicherungsschutzKasko,
  Vollkasko
} from '@/types/policy/PolicyTypes'
import { type PolicyMotorDetails } from '@/types/motor/PolicyMotorDetails'

export function printPremium(premium: number): string {
  return `${premium.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`
}

export function getInsuranceCover(motorContract: MotorContract): InsuranceCover {
  return {
    comprehensiveCover: getKaskodeckung(motorContract.comprehensiveInsurance),
    liabilityBasicDiscountYear: motorContract.liabilityInsurance.basicDiscountYear,
    deductible: getSelbstbeteiligung(motorContract.comprehensiveInsurance),
    oldExtras: getExtras(motorContract),
    newExtras: getExtras(motorContract)
  }
}

export function getSelbstbeteiligung(
  kasko: VersicherungsschutzKasko | undefined
): Deductible {
  if (kasko === undefined) {
    return {}
  } else if (instanceOfVollkasko(kasko)) {
    const vollkasko = kasko as Vollkasko
    return {
      deductiblePartiallyComprehensive:
        vollkasko.deductibleOfPartiallyComprehensiveInsurance?.amount,
      deductibleFullyComprehensive:
        vollkasko.deductibleOfFullyComprehensiveInsurance?.amount
    }
  } else {
    const teilkasko = kasko as Teilkasko
    return {
      deductiblePartiallyComprehensive:
        teilkasko.deductibleOfPartiallyComprehensiveInsurance?.amount
    }
  }
}

const haftpflichtExtras: HaftpflichtExtras = {
  RABATTSCHUTZ: false,
  FAHRERSCHUTZ: false
}

const kaskoExtras: KaskoExtras = {
  ERSATZWAGEN: false,
  ERWEITERTE_NEUWERTENTSCHAEDIGUNG: false,
  FREIE_WERKSTATTWAHL: false,
  GAP_DECKUNG: false,
  KAUFPREISENTSCHAEDIGUNG_FUER_GEBRAUCHTWAGEN: false,
  RABATTSCHUTZ: false,
  TIERBISSFOLGESCHAEDEN_GEMAESS_AKB: false,
  VERSICHERUNGSSCHUTZ_BEI_GROBER_FAHRLAESSIGKEIT: false,
  ZUSAMMENSTOSS_MIT_ALLEN_TIEREN: false
}

export function getExtras(motorContract: MotorContract): Extras {
  const kasko: VersicherungsschutzKasko | undefined = motorContract.comprehensiveInsurance
  const haftpflicht: VersicherungsschutzHaftpflicht = motorContract.liabilityInsurance

  const extras: Extras = {
    liabilityProduct: {
      productKey: haftpflicht.productKey,
      productTarif: haftpflicht.tarif,
      extras: haftpflichtExtras
    },
    comprehensiveInsuranceProduct: {
      productKey: '',
      extras: {
        ERSATZWAGEN: false,
        ERWEITERTE_NEUWERTENTSCHAEDIGUNG: false,
        FREIE_WERKSTATTWAHL: false,
        GAP_DECKUNG: false,
        KAUFPREISENTSCHAEDIGUNG_FUER_GEBRAUCHTWAGEN: false,
        RABATTSCHUTZ: false,
        TIERBISSFOLGESCHAEDEN_GEMAESS_AKB: false,
        VERSICHERUNGSSCHUTZ_BEI_GROBER_FAHRLAESSIGKEIT: false,
        ZUSAMMENSTOSS_MIT_ALLEN_TIEREN: false
      }
    }
  }

  if (haftpflicht.extras) {
    extras.liabilityProduct.extras = haftpflicht.extras
  }

  if (kasko !== undefined) {
    extras.comprehensiveInsuranceProduct = {
      productKey: kasko.productKey,
      extras: kaskoExtras
    }

    for (const item of Object.keys(kaskoExtras)) {
      extras.comprehensiveInsuranceProduct.extras[item as keyof KaskoExtras] = kasko
        .extras[item as keyof KaskoExtras]
        ? kasko.extras[item as keyof KaskoExtras]
        : false
    }
  }
  return extras
}

export function getKaskodeckung(
  kasko: VersicherungsschutzKasko | undefined
): ComprehensiveCover {
  if (kasko === undefined) {
    return ComprehensiveCover.NO_COMPREHENSIVE
  }
  if (instanceOfVollkasko(kasko)) {
    return ComprehensiveCover.FULLY_COMPREHENSIVE
  }
  return ComprehensiveCover.PARTIALLY_COMPREHENSIVE
}

function instanceOfVollkasko(object: any): object is Vollkasko {
  return 'noClaimsClass' in object
}

export function getCircleOfDrivers(motorContract: MotorContract): Fahrerkreis {
  return {
    circleOfDriversType: motorContract.circleOfDrivers.circleOfDriversType,
    vehicleUsage: motorContract.circleOfDrivers.vehicleUsage,
    birthYearPartnerDriver: motorContract.circleOfDrivers.birthYearPartnerDriver
      ? motorContract.circleOfDrivers.birthYearPartnerDriver
      : 0,
    birthYearYoungestDriver: motorContract.circleOfDrivers.birthYearYoungestDriver
      ? motorContract.circleOfDrivers.birthYearYoungestDriver
      : 0,
    birthYearOldestDriver: motorContract.circleOfDrivers.birthYearOldestDriver
      ? motorContract.circleOfDrivers.birthYearOldestDriver
      : 0
  }
}

export interface OptionDynamicValue<T> {
  label: string
  value: T
}

export function isVersicherungsumfangChangeAllowed(
  motorDetails: PolicyMotorDetails
): boolean {
  return motorDetails.isChangingInsuranceCoverAllowed
}

export function removeGettersSetters(object: any): any {
  const obj = JSON.parse(JSON.stringify(object))
  Object.keys(obj).forEach((key) => {
    if (obj['_' + key] === obj[key]) {
      delete obj['_' + key]
    }
    return obj
  })
  return obj
}

export function formatKilometer(kilometers: number): string {
  if (kilometers === undefined || kilometers === null) {
    return ''
  } else {
    return `${kilometers.toLocaleString('de-DE')} km`
  }
}

/*eslint-disable */
export function areTwoObjectsWithouthGetterSettersEqual(
  object1: any,
  object2: any
): boolean {
  return (
    JSON.stringify(removeGettersSetters(object1)) ===
    JSON.stringify(removeGettersSetters(object2))
  )
}

/*eslint-enable */

export function checkIfAtleastOneKaskoExtraIsChecked(
  comprehensiveInsuranceProduct: KaskoProduct
): boolean {
  let numberOfKaskoExtrasChecked = 0
  for (const item of Object.keys(kaskoExtras)) {
    const item1 = comprehensiveInsuranceProduct.extras[item as keyof KaskoExtras]
    numberOfKaskoExtrasChecked += item1 ? 1 : 0
  }
  return numberOfKaskoExtrasChecked > 0
}

export function areKaskoExtrasEqual(
  kaskoExtras1: KaskoExtras,
  kaskoExtras2: KaskoExtras
): boolean {
  let allComprehensiveInsuranceProductsEqual = true
  for (const item of Object.keys(kaskoExtras)) {
    if (allComprehensiveInsuranceProductsEqual) {
      const item1 = kaskoExtras1[item as keyof KaskoExtras]
      const item2 = kaskoExtras2[item as keyof KaskoExtras]
      // this statements treats undefined same as false in case of the enum items
      allComprehensiveInsuranceProductsEqual =
        item1 === undefined
          ? item2 === undefined || !item2
          : item2 !== undefined
            ? item1 === item2
            : !item1
    }
  }
  return allComprehensiveInsuranceProductsEqual
}

export function areTwoMotorContractsEqual(
  contract1: MotorContractCoverageChange,
  contract2: MotorContractCoverageChange
): boolean {
  const circleOfDriversEqual: boolean = areTwoObjectsWithouthGetterSettersEqual(
    contract1?.circleOfDrivers,
    contract2?.circleOfDrivers
  )
  const mileagesEqual: boolean = areTwoObjectsWithouthGetterSettersEqual(
    contract1.mileage,
    contract2.mileage
  )
  const insuranceCoverEqual = areTwoInsuranceCoverEqual(
    contract1.insuranceCover,
    contract2.insuranceCover
  )

  return circleOfDriversEqual && mileagesEqual && insuranceCoverEqual
}

function areTwoInsuranceCoverEqual(
  insuranceCover1: InsuranceCover,
  insuranceCover2: InsuranceCover
): boolean {
  const isComprehensiveCoverEqual =
    insuranceCover1.comprehensiveCover === insuranceCover2.comprehensiveCover
  const isliabilityBasicDiscountYearEqual =
    insuranceCover1.liabilityBasicDiscountYear ===
    insuranceCover2.liabilityBasicDiscountYear
  const isDeductibleEqual = areTwoObjectsWithouthGetterSettersEqual(
    insuranceCover1.deductible,
    insuranceCover2.deductible
  )
  const areNewExtrasEqual = areTwoInsuranceExtrasEqual(
    insuranceCover1.newExtras,
    insuranceCover2.newExtras
  )
  const areOldExtrasEqual = areTwoInsuranceExtrasEqual(
    insuranceCover1.oldExtras,
    insuranceCover2.oldExtras
  )

  return (
    isComprehensiveCoverEqual &&
    isliabilityBasicDiscountYearEqual &&
    isDeductibleEqual &&
    areNewExtrasEqual &&
    areOldExtrasEqual
  )
}

function areTwoInsuranceExtrasEqual(extra1: Extras, extra2: Extras): boolean {
  const liabilityProductEqual = areLiablityProductsEqual(
    extra1.liabilityProduct,
    extra2.liabilityProduct
  )
  const comprehensiveInsuranceProductEqual =
    extra1.comprehensiveInsuranceProduct && extra2.comprehensiveInsuranceProduct
      ? areComprehensiveInsuranceProductEqual(
          extra1.comprehensiveInsuranceProduct,
          extra2.comprehensiveInsuranceProduct
        )
      : false

  return liabilityProductEqual && comprehensiveInsuranceProductEqual
}

function areComprehensiveInsuranceProductEqual(
  comprehensiveInsuranceProduct1: KaskoProduct,
  comprehensiveInsuranceProduct2: KaskoProduct
): boolean {
  const productKeyEqual =
    comprehensiveInsuranceProduct1.productKey ===
    comprehensiveInsuranceProduct2.productKey

  return (
    productKeyEqual &&
    areKaskoExtrasEqual(
      comprehensiveInsuranceProduct1.extras,
      comprehensiveInsuranceProduct2.extras
    )
  )
}

function areLiablityProductsEqual(
  liablityProduct1: HaftpflichtProduct,
  liablityProduct2: HaftpflichtProduct
) {
  const productKeyEqual = liablityProduct1.productKey === liablityProduct2.productKey

  return (
    productKeyEqual &&
    areHaftpflichtExtrasEqual(liablityProduct1.extras, liablityProduct2.extras)
  )
}

export function areHaftpflichtExtrasEqual(
  haftpflichtExtra1: HaftpflichtExtras,
  haftpflichtExtra2: HaftpflichtExtras
): boolean {
  let allLiablityProductsAreEqual = true
  for (const item of Object.keys(haftpflichtExtras)) {
    if (allLiablityProductsAreEqual) {
      const item1 = haftpflichtExtra1[item as keyof HaftpflichtExtras]
      const item2 = haftpflichtExtra2[item as keyof HaftpflichtExtras]
      // this statements treats undefined same as false in case of the enum items
      allLiablityProductsAreEqual =
        item1 === undefined
          ? item2 === undefined || !item2
          : item2 !== undefined
            ? item1 === item2
            : !item1
    }
  }
  return allLiablityProductsAreEqual
}
